// 10 SEO-friendly detailed recipes for NutrifyMe
export const recipes = [
  {
    slug: 'moong-dal-chilla-paneer',
    name: 'Moong Dal Chilla with Paneer',
    tag: 'Breakfast',
    time: '20 min',
    calories: 320,
    protein: 22,
    servings: 2,
    image: 'https://images.unsplash.com/photo-1667499745120-f9bcef8f584e?w=1200&q=80',
    description: 'High-protein Indian breakfast that keeps blood sugar stable for hours. A NutrifyMe client favourite.',
    ingredients: [
      '1 cup yellow moong dal (soaked 4 hours)',
      '100g paneer, crumbled',
      '1 small onion, finely chopped',
      '1 green chilli, finely chopped',
      '1 tsp grated ginger',
      '1/2 tsp cumin seeds',
      '1/4 tsp turmeric',
      'Salt to taste',
      '2 tsp ghee',
      'Fresh coriander, chopped',
    ],
    steps: [
      'Drain the soaked moong dal and blend to a smooth batter with a little water and salt.',
      'Add cumin, turmeric, ginger, chilli and coriander to the batter and mix well.',
      'Heat a non-stick tawa and spread a ladle of batter into a thin disc.',
      'Sprinkle crumbled paneer and onion over the chilla and drizzle a little ghee around the edges.',
      'Cook for 2 to 3 minutes until golden, then flip and cook for another minute.',
      'Serve hot with mint chutney and a glass of buttermilk.',
    ],
    tips: [
      'Do not skip the soaking step — it improves digestion and reduces cooking time.',
      'For a vegan version, use tofu instead of paneer.',
    ],
  },
  {
    slug: 'ragi-chocolate-smoothie',
    name: 'Ragi Chocolate Smoothie',
    tag: 'Breakfast',
    time: '5 min',
    calories: 240,
    protein: 14,
    servings: 1,
    image: 'https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?w=1200&q=80',
    description: 'A fibre and calcium powerhouse dressed up as a chocolate smoothie. Ready in 5 minutes.',
    ingredients: [
      '2 tbsp sprouted ragi flour',
      '1 cup unsweetened almond milk',
      '1 tbsp cocoa powder',
      '1 date, pitted',
      '1 tbsp peanut butter',
      '1 scoop whey isolate (optional)',
      'A pinch of cinnamon',
      '4 ice cubes',
    ],
    steps: [
      'Roast the ragi flour on low flame for 2 minutes and cool.',
      'Blend all ingredients until smooth and creamy.',
      'Pour into a chilled glass and top with a light dust of cinnamon.',
    ],
    tips: [
      'Use jaggery instead of the date for a stronger caramel note.',
      'Add a tablespoon of chia to boost fibre further.',
    ],
  },
  {
    slug: 'millet-poha-bowl',
    name: 'Millet Poha Bowl',
    tag: 'Breakfast',
    time: '15 min',
    calories: 310,
    protein: 12,
    servings: 2,
    image: 'https://images.unsplash.com/photo-1606858374191-c18040e98ad7?w=1200&q=80',
    description: 'A twist on classic poha using foxtail or barnyard millet — lower glycemic and higher in fibre.',
    ingredients: [
      '1 cup foxtail millet flakes (or barnyard millet)',
      '1 medium onion, chopped',
      '1 small potato, diced',
      '1/4 cup roasted peanuts',
      '1 green chilli, slit',
      'Curry leaves',
      '1 tsp mustard seeds',
      '1/2 tsp turmeric',
      'Lemon juice, salt, coriander to taste',
      '2 tsp ghee or peanut oil',
    ],
    steps: [
      'Rinse the millet flakes gently and drain — do not soak.',
      'Heat ghee in a kadhai, splutter mustard seeds, curry leaves and chilli.',
      'Add onion and potato and saute till potato is tender.',
      'Add turmeric and salt, then fold in the millet flakes and peanuts.',
      'Cover and steam for 3 minutes on low flame.',
      'Finish with lemon juice and coriander.',
    ],
    tips: [
      'Pair with a boiled egg or a glass of buttermilk to hit 20g protein.',
      'Add finely chopped tomatoes for extra freshness.',
    ],
  },
  {
    slug: 'overnight-oats-chia',
    name: 'Overnight Oats with Chia and Berries',
    tag: 'Breakfast',
    time: '5 min prep + overnight',
    calories: 340,
    protein: 16,
    servings: 1,
    image: 'https://images.pexels.com/photos/4929677/pexels-photo-4929677.jpeg?auto=compress&w=1200',
    description: 'A grab-and-go breakfast that beats every packaged cereal on protein, fibre and satiety.',
    ingredients: [
      '1/2 cup rolled oats',
      '1 tbsp chia seeds',
      '1 cup greek yogurt or curd',
      '1/2 cup unsweetened almond milk',
      '1 tsp maple syrup or 1 chopped date',
      '2 tbsp mixed berries',
      '1 tbsp chopped walnuts',
    ],
    steps: [
      'Combine oats, chia, yogurt, milk and sweetener in a jar and stir well.',
      'Cover and refrigerate overnight (at least 6 hours).',
      'In the morning, top with berries and walnuts and dig in.',
    ],
    tips: [
      'Add a scoop of unflavoured whey isolate for 25g protein.',
      'Chocolate variation: add 1 tbsp cocoa powder and a chopped square of 85% dark chocolate.',
    ],
  },
  {
    slug: 'jowar-veggie-khichdi',
    name: 'Jowar Veggie Khichdi',
    tag: 'Dinner',
    time: '30 min',
    calories: 380,
    protein: 15,
    servings: 2,
    image: 'https://images.unsplash.com/photo-1594631252845-29fc4cc8cde9?w=1200&q=80',
    description: 'A gut-friendly, one-pot dinner our PCOS clients love. Easy to digest and deeply nourishing.',
    ingredients: [
      '1/2 cup jowar (broken or whole, soaked)',
      '1/4 cup yellow moong dal',
      '1/4 cup mixed vegetables (carrot, beans, peas)',
      '1 tomato, chopped',
      '1/2 tsp ginger paste',
      '1/4 tsp turmeric',
      '1/2 tsp cumin seeds',
      'A pinch of hing',
      '2 tsp ghee',
      'Salt and coriander to taste',
    ],
    steps: [
      'Pressure cook jowar, dal, vegetables, ginger, turmeric and salt with 3.5 cups water for 4 to 5 whistles.',
      'In a small pan, heat ghee and splutter cumin seeds and hing.',
      'Pour the tempering over the khichdi and stir gently.',
      'Serve warm with a spoon of curd or homemade tomato chutney.',
    ],
    tips: [
      'Add a spoon of extra ghee at serving for satiety and vitamin A absorption.',
      'A bowl of salad or homemade pickle on the side rounds out the meal beautifully.',
    ],
  },
  {
    slug: 'paneer-tikka-lettuce-wraps',
    name: 'Paneer Tikka Lettuce Wraps',
    tag: 'Dinner',
    time: '25 min',
    calories: 350,
    protein: 26,
    servings: 2,
    image: 'https://images.pexels.com/photos/15319037/pexels-photo-15319037.jpeg?auto=compress&w=1200',
    description: 'A high-protein, low-carb dinner that satisfies the paneer craving without the naan.',
    ingredients: [
      '200g paneer, cubed',
      '1/2 cup thick curd',
      '2 tsp tikka masala',
      '1 tsp ginger-garlic paste',
      '1 tbsp mustard oil',
      'Salt and lemon juice',
      '8 large lettuce or cabbage leaves',
      '1 small onion, sliced',
      'Mint chutney',
    ],
    steps: [
      'Marinate paneer with curd, tikka masala, ginger-garlic paste, mustard oil, salt and lemon for 20 minutes.',
      'Grill or air-fry the paneer skewers at 200°C for 12 minutes, turning halfway.',
      'Wash and pat dry the lettuce leaves.',
      'Place paneer, onion slices and a spoon of mint chutney inside each leaf and roll up.',
    ],
    tips: [
      'Swap paneer with tofu or chicken tikka for variety.',
      'Serve with a side of cucumber-tomato salad to complete the meal.',
    ],
  },
  {
    slug: 'sattu-drink-lemon',
    name: 'Sattu Lemon Cooler',
    tag: 'Snack',
    time: '5 min',
    calories: 180,
    protein: 12,
    servings: 1,
    image: 'https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?w=1200&q=80',
    description: 'A hydrating, protein-rich drink perfect for the 4pm slump. Great for gut and blood sugar.',
    ingredients: [
      '2 tbsp roasted sattu (chickpea flour)',
      '1 glass cool water',
      '1 tsp lemon juice',
      '1/2 tsp roasted cumin powder',
      'Salt or black salt to taste',
      'Fresh mint (optional)',
    ],
    steps: [
      'Whisk sattu with a little water to make a smooth paste.',
      'Add the rest of the water, lemon, cumin, mint and salt.',
      'Serve chilled.',
    ],
    tips: [
      'For a sweet version, use jaggery water and skip the lemon.',
      'Add a spoon of chia for a satiating pre-workout drink.',
    ],
  },
  {
    slug: 'gut-reset-buttermilk',
    name: 'Gut-Reset Herbed Buttermilk',
    tag: 'Snack',
    time: '3 min',
    calories: 90,
    protein: 5,
    servings: 1,
    image: 'https://images.unsplash.com/photo-1667499745120-f9bcef8f584e?w=1200&q=80',
    description: 'The traditional Indian probiotic drink, upgraded with ginger, curry leaves and hing for max gut love.',
    ingredients: [
      '3/4 cup homemade curd',
      '1/2 cup chilled water',
      '1 tsp grated ginger',
      '4 curry leaves',
      'A pinch of hing (asafoetida)',
      '1/2 tsp roasted cumin powder',
      'Salt or black salt',
      'Fresh coriander',
    ],
    steps: [
      'Blend curd and water till smooth and frothy.',
      'Bruise ginger and curry leaves in a mortar and add to the buttermilk.',
      'Add hing, cumin, salt and coriander.',
      'Sip slowly after a heavy meal.',
    ],
    tips: [
      'Perfect after Sunday biryani or heavy Indian meals.',
      'Skip the ginger if you have severe acidity.',
    ],
  },
  {
    slug: 'baked-methi-muthiya',
    name: 'Baked Methi Muthiya',
    tag: 'Snack',
    time: '35 min',
    calories: 220,
    protein: 9,
    servings: 3,
    image: 'https://images.pexels.com/photos/4929677/pexels-photo-4929677.jpeg?auto=compress&w=1200',
    description: 'A guilt-free Gujarati snack — baked, not fried, and packed with fibre, iron and calcium.',
    ingredients: [
      '1 cup fresh methi leaves, chopped',
      '1 cup whole wheat flour',
      '1/4 cup besan',
      '1 tsp ginger-chilli paste',
      '1/4 tsp turmeric',
      '1 tsp roasted sesame seeds',
      '1 tbsp curd',
      '1 tsp oil',
      'Salt, ajwain and hing to taste',
    ],
    steps: [
      'Mix all ingredients into a soft dough with minimal water. Rest 10 minutes.',
      'Shape into cylinders and place on a lined baking tray.',
      'Bake at 180°C for 20 to 25 minutes till golden.',
      'Cool, slice into rounds, and temper briefly with mustard seeds and curry leaves.',
    ],
    tips: [
      'Pair with a small bowl of curd for extra protein.',
      'Freezes well — batch cook for busy weeks.',
    ],
  },
  {
    slug: 'turmeric-golden-milk',
    name: 'Turmeric Golden Milk',
    tag: 'Dinner',
    time: '8 min',
    calories: 130,
    protein: 6,
    servings: 1,
    image: 'https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?w=1200&q=80',
    description: 'The classic anti-inflammatory nightcap. Great for sleep, joints, gut and skin.',
    ingredients: [
      '1 cup full-fat milk (or oat milk)',
      '1/4 tsp fresh grated turmeric (or 1/8 tsp powder)',
      '1 small pinch of black pepper',
      '1/4 tsp cinnamon',
      '2 crushed cardamom pods',
      '1/2 tsp ghee',
      'A tiny piece of jaggery (optional)',
    ],
    steps: [
      'Warm milk gently on low flame with all the spices.',
      'Simmer for 5 minutes without letting it boil aggressively.',
      'Strain and add jaggery if using.',
      'Sip slowly 30 to 60 minutes before bed.',
    ],
    tips: [
      'Black pepper multiplies turmeric absorption by 2,000% — do not skip it.',
      'Use jaggery only if you tolerate it well; skip if reducing sugar strictly.',
    ],
  },
];

export const getRecipeBySlug = (slug) => recipes.find((r) => r.slug === slug);
